/**
 * Vorlage „Stäbchenprobe“.
 *
 * Ein Holzstäbchen wird in die Mitte des Kuchens gestochen und wieder
 * herausgezogen. Eine Lupe zeigt die Spitze: Bleibt kein Teig daran, ist der
 * Kuchen fertig. Klebt noch Teig, braucht er noch einige Minuten.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { circle, compact, faded, group, line } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { cake } from '../parts/food.ts';
import { arrow, badge, counter, doneMark, GROUND_Y, shadow } from '../parts/stage.ts';
import { place, skewer } from '../parts/utensils.ts';
import { cakeTin } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

const TIN = { cx: 92, width: 112, height: 26 } as const;
const TIN_RY = TIN.width * 0.16;
const RIM_Y = GROUND_Y - TIN.height - TIN_RY;
/** Lupe mit der Stäbchenspitze. */
const LENS = { x: 190, y: 66, r: 30 } as const;

/** Zeitplan. */
const TIME = {
  appear: [0.02, 0.08],
  in: [0.08, 0.24],
  out: [0.36, 0.52],
  lens: [0.54, 0.62],
  result: [0.62, 0.68],
  vanish: [0.88, 0.96],
} as const;

/** Teigreste an der Spitze. */
function crumbs(scale: number): SceneNode[] {
  return [
    circle(-1.6 * scale, -6 * scale, 1.6 * scale, { fill: 'deep', stroke: 'ink', strokeWidth: STROKE.fine }),
    circle(1.4 * scale, -10 * scale, 1.4 * scale, { fill: 'deep', stroke: 'ink', strokeWidth: STROKE.fine }),
    circle(-1.2 * scale, -14 * scale, 1.2 * scale, { fill: 'deep', stroke: 'ink', strokeWidth: STROKE.fine }),
  ];
}

/** Vorlage „Stäbchenprobe“. */
export const bakeSkewerTemplate = defineTemplate({
  id: 'bake.skewer-test',
  group: 'bake',
  title: 'Stäbchenprobe',
  description:
    'Ein Holzstäbchen wird in die Mitte des Kuchens gestochen. Bleibt beim Herausziehen kein feuchter Teig daran, ist der Kuchen durchgebacken.',
  durationSeconds: 6,
  keywords: [
    'stäbchenprobe',
    'holzstäbchen',
    'garprobe',
    'zahnstocher',
    'kein teig',
    'teig kleben',
    'stäbchen',
  ],
  stillTime: 0.75,
  parameters: [
    choice('result', 'Ergebnis', { clean: 'sauber – fertig', sticky: 'klebt – weiterbacken' }, 'clean'),
  ],
  scene(values, t) {
    const sticky = textValue(values, 'result', 'clean') === 'sticky';
    const tin = cakeTin(TIN.cx, RIM_Y, TIN.width, TIN.height);
    const appear = ease.outCubic(segment(t, ...TIME.appear)) * (1 - ease.inCubic(segment(t, ...TIME.vanish)));
    const depth = ease.inOutCubic(segment(t, ...TIME.in)) - ease.inOutCubic(segment(t, ...TIME.out));
    const tipY = lerp(RIM_Y - 30, RIM_Y + 14, depth);
    const pulledOut = t > TIME.out[1];
    const lens = ease.outBack(segment(t, ...TIME.lens)) * (1 - segment(t, ...TIME.vanish));
    const result = segment(t, ...TIME.result) * (1 - segment(t, ...TIME.vanish));

    // Lupe: vergrößerte Spitze des Stäbchens.
    const lensNode: SceneNode | null =
      lens > 0.01
        ? group(
            [
              line(TIN.cx + 12, RIM_Y - 36, LENS.x - LENS.r * 0.8, LENS.y + LENS.r * 0.4, {
                stroke: 'deep',
                strokeWidth: STROKE.fine,
                dash: '3 3',
              }),
              circle(LENS.x, LENS.y, LENS.r, { fill: 'paper', stroke: 'ink', strokeWidth: STROKE.outline }),
              group([place(LENS.x, LENS.y + 18, 0, [...skewer(), ...(sticky ? crumbs(1) : [])], 2.4)], {
                clip: `M${LENS.x - LENS.r + 2} ${LENS.y}a${LENS.r - 2} ${LENS.r - 2} 0 1 0 ${(LENS.r - 2) * 2} 0a${LENS.r - 2} ${LENS.r - 2} 0 1 0 ${-(LENS.r - 2) * 2} 0Z`,
              }),
            ],
            { transform: { scale: lens, ox: LENS.x, oy: LENS.y } },
          )
        : null;
    return compact([
      ...counter(),
      shadow(TIN.cx, TIN.width * 0.42),
      ...tin.back,
      faded(appear, [
        place(TIN.cx + 8, tipY, 0, [...skewer(), ...(sticky && pulledOut ? crumbs(1) : [])], 0.8),
      ]),
      ...cake(TIN.cx, RIM_Y + 6, TIN.width - 6, 8, 6),
      ...tin.front,
      lensNode,
      faded(result, [
        sticky
          ? badge(LENS.x, LENS.y + LENS.r + 18, '+5 Min')
          : doneMark(LENS.x + LENS.r * 0.72, LENS.y + LENS.r * 0.72, 1.1),
      ]),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: TIN.cx - 10, y: RIM_Y - 54 },
        { x: TIN.cx - 10, y: RIM_Y - 28 },
      ]),
      arrow([
        { x: TIN.cx + 26, y: RIM_Y - 28 },
        { x: TIN.cx + 26, y: RIM_Y - 54 },
      ]),
    ];
  },
});
